/**
 * Publisher-owned namespaces are verbatim: our builders only mint mindforge.health.
 * HTTP is canonical for HL7, LOINC, UCUM and SNOMED; upgrading the scheme would
 * silently create a different namespace, not secure the existing one.
 */
declare const foreignBrand: unique symbol;
export type ForeignSystemUrl = string & { readonly [foreignBrand]: "ForeignSystemUrl" };

// HL7 owns these terminology code systems.
export const ALLERGY_CLINICAL_SYSTEM =
  "http://terminology.hl7.org/CodeSystem/allergyintolerance-clinical" as ForeignSystemUrl;
export const ALLERGY_VERIFICATION_SYSTEM =
  "http://terminology.hl7.org/CodeSystem/allergyintolerance-verification" as ForeignSystemUrl;
/** `AuditEvent.type`. The subtypes we define ourselves live in `code-systems.ts`. */
export const AUDIT_EVENT_TYPE_SYSTEM = "http://terminology.hl7.org/CodeSystem/audit-event-type" as ForeignSystemUrl;
export const CONDITION_CATEGORY_SYSTEM = "http://terminology.hl7.org/CodeSystem/condition-category" as ForeignSystemUrl;
export const CONDITION_CLINICAL_SYSTEM = "http://terminology.hl7.org/CodeSystem/condition-clinical" as ForeignSystemUrl;
export const CONDITION_VERIFICATION_SYSTEM =
  "http://terminology.hl7.org/CodeSystem/condition-ver-status" as ForeignSystemUrl;
export const DATA_ABSENT_REASON_SYSTEM = "http://terminology.hl7.org/CodeSystem/data-absent-reason" as ForeignSystemUrl;
export const FLAG_CATEGORY_SYSTEM = "http://terminology.hl7.org/CodeSystem/flag-category" as ForeignSystemUrl;
export const GOAL_ACHIEVEMENT_SYSTEM = "http://terminology.hl7.org/CodeSystem/goal-achievement" as ForeignSystemUrl;
export const GOAL_PRIORITY_SYSTEM = "http://terminology.hl7.org/CodeSystem/goal-priority" as ForeignSystemUrl;
export const OBSERVATION_CATEGORY_SYSTEM =
  "http://terminology.hl7.org/CodeSystem/observation-category" as ForeignSystemUrl;
export const PLAN_DEFINITION_TYPE_SYSTEM =
  "http://terminology.hl7.org/CodeSystem/plan-definition-type" as ForeignSystemUrl;
export const ACT_CODE_SYSTEM = "http://terminology.hl7.org/CodeSystem/v3-ActCode" as ForeignSystemUrl;
export const CONSENT_SCOPE_SYSTEM = "http://terminology.hl7.org/CodeSystem/consentscope" as ForeignSystemUrl;
export const PARTICIPATION_TYPE_SYSTEM =
  "http://terminology.hl7.org/CodeSystem/v3-ParticipationType" as ForeignSystemUrl;
export const ROLE_CLASS_SYSTEM = "http://terminology.hl7.org/CodeSystem/v3-RoleClass" as ForeignSystemUrl;
export const ROLE_CODE_SYSTEM = "http://terminology.hl7.org/CodeSystem/v3-RoleCode" as ForeignSystemUrl;
/** `RelatedPerson.relationship` contact roles: emergency contact, other. */
export const CONTACT_ROLE_SYSTEM = "http://terminology.hl7.org/CodeSystem/v2-0131" as ForeignSystemUrl;
/** `Timing.code` — BID, TID, QD and the like. */
export const TIMING_ABBREVIATION_SYSTEM =
  "http://terminology.hl7.org/CodeSystem/v3-GTSAbbreviation" as ForeignSystemUrl;

// HL7 owns the core and SDC extension and identifier namespaces.
export const MAX_VALUE_URL = "http://hl7.org/fhir/StructureDefinition/maxValue" as ForeignSystemUrl;
export const MIN_VALUE_URL = "http://hl7.org/fhir/StructureDefinition/minValue" as ForeignSystemUrl;
export const OPTION_EXCLUSIVE_URL =
  "http://hl7.org/fhir/StructureDefinition/questionnaire-optionExclusive" as ForeignSystemUrl;
export const SLIDER_STEP_VALUE_URL =
  "http://hl7.org/fhir/StructureDefinition/questionnaire-sliderStepValue" as ForeignSystemUrl;
export const QUESTIONNAIRE_UNIT_URL = "http://hl7.org/fhir/StructureDefinition/questionnaire-unit" as ForeignSystemUrl;
export const QUESTIONNAIRE_ITEM_CONTROL_URL =
  "http://hl7.org/fhir/StructureDefinition/questionnaire-itemControl" as ForeignSystemUrl;
export const QUESTIONNAIRE_ITEM_CONTROL_SYSTEM = "http://hl7.org/fhir/questionnaire-item-control" as ForeignSystemUrl;
export const TIMEZONE_EXTENSION_URL = "http://hl7.org/fhir/StructureDefinition/timezone" as ForeignSystemUrl;
export const ICD_10_CM_SYSTEM = "http://hl7.org/fhir/sid/icd-10-cm" as ForeignSystemUrl;
export const NPI_SYSTEM = "http://hl7.org/fhir/sid/us-npi" as ForeignSystemUrl;
export const INITIAL_EXPRESSION_URL =
  "http://hl7.org/fhir/uv/sdc/StructureDefinition/sdc-questionnaire-initialExpression" as ForeignSystemUrl;
export const ITEM_POPULATION_CONTEXT_URL =
  "http://hl7.org/fhir/uv/sdc/StructureDefinition/sdc-questionnaire-itemPopulationContext" as ForeignSystemUrl;

// Medplum owns these extension, reference and value-set namespaces.
export const SERVICE_TYPE_REFERENCE_URL = "https://medplum.com/fhir/service-type-reference" as ForeignSystemUrl;
export const SCHEDULING_PARAMS_URL =
  "https://medplum.com/fhir/StructureDefinition/SchedulingParameters" as ForeignSystemUrl;
export const TASK_ELEMENTS_URL = "https://medplum.com/fhir/StructureDefinition/task-elements" as ForeignSystemUrl;
export const FHIR_PATH_CRITERIA_EXT_URL =
  "https://medplum.com/fhir/StructureDefinition/fhir-path-criteria-expression" as ForeignSystemUrl;
export const SUPPORTED_INTERACTION_URL =
  "https://medplum.com/fhir/StructureDefinition/subscription-supported-interaction" as ForeignSystemUrl;
export const RESOURCE_TYPES_VALUESET_URL = "https://medplum.com/fhir/ValueSet/resource-types" as ForeignSystemUrl;

// Regenstrief Institute owns LOINC. loinc.org is the system; fhir.loinc.org serves network endpoints and is deliberately absent.
export const LOINC_SYSTEM = "http://loinc.org" as ForeignSystemUrl;

// Regenstrief Institute owns the UCUM units namespace.
export const UCUM_SYSTEM = "http://unitsofmeasure.org" as ForeignSystemUrl;

// SNOMED International owns the clinical terminology namespace.
export const SNOMED_SYSTEM = "http://snomed.info/sct" as ForeignSystemUrl;

// The National Library of Medicine owns RxNorm. The system URL is the docs page, verbatim, per HL7.
export const RXNORM_SYSTEM = "http://www.nlm.nih.gov/research/umls/rxnorm" as ForeignSystemUrl;

// GoHighLevel owns the contact identifier namespace.
export const GHL_CONTACT_ID_SYSTEM = "https://gohighlevel.com/contact-id" as ForeignSystemUrl;
export const MATCH_GRADE_URL = "http://hl7.org/fhir/StructureDefinition/match-grade" as ForeignSystemUrl;

export const ORDINAL_VALUE_EXTENSION_URL = "http://hl7.org/fhir/StructureDefinition/ordinalValue" as ForeignSystemUrl;
